'use client'

import { useState, type ReactNode } from 'react'

import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form'
import Swal from 'sweetalert2'
import { z } from 'zod'

import { zodResolver } from '@hookform/resolvers/zod'

import { useAuthContext } from '@/features/auth/contexts/AuthContext'
import { cn } from '@/shared/lib/utils'

import { validationSchema } from './Fields/validationSchema'

type ResetPasswordFormData = z.infer<typeof validationSchema>

interface ResetPasswordFormProps {
  onResetPassword?: (result: { ok: boolean }) => void
  onError?: (result: { ok: boolean; error?: string }) => void
  className?: string
  children?: ReactNode
}

export function ResetPasswordForm({
  onResetPassword,
  onError,
  className = '',
  children
}: ResetPasswordFormProps) {
  const { httpClient, goToSignIn, isLoading, setIsLoading, currentEmail } =
    useAuthContext()
  const [tokenValidated, setTokenValidated] = useState<string | false>(false)
  const [token, setToken] = useState('')

  const methods = useForm<ResetPasswordFormData>({
    defaultValues: { password: '', passwordConfirmation: '' },
    resolver: zodResolver(validationSchema)
  })

  const handleResetPassword: SubmitHandler<ResetPasswordFormData> = async data => {
    if (isLoading) return
    setIsLoading(true)

    try {
      await httpClient.put('/auth/reset-password', {
        email: currentEmail,
        token: tokenValidated,
        password: data.password,
        password_confirmation: data.passwordConfirmation
      })

      onResetPassword?.({ ok: true })
      if (!onResetPassword) {
        await Swal.fire({
          icon: 'success',
          title: 'Senha redefinida com sucesso!',
          text: 'Você já pode fazer o login com a sua nova senha.'
        })
        goToSignIn()
      }
    } catch (err: any) {
      onError?.({ ok: false, error: err?.message })

      const tokenError =
        err?.response?.data?.errors?.reset_password_token ||
        err?.errors?.reset_password_token ||
        err?.reset_password_token

      if (tokenError) {
        setTokenValidated(false)
      }

      if (!onError) {
        await Swal.fire({
          icon: 'error',
          title: 'Ocorreu um erro',
          text: 'Não foi possível redefinir a sua senha. Verifique o código e tente novamente.'
        })
      }
    }

    setIsLoading(false)
  }

  if (!currentEmail) {
    return <span>E-mail não presente</span>
  }

  if (!tokenValidated) {
    return (
      <div className={cn('grid gap-6', className)}>
        <div className="grid gap-2">
          <label className="text-sm font-medium text-foreground">
            Digite o código de redefinição
          </label>
          <input
            value={token}
            onChange={event => setToken(event.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full rounded-lg border px-3 py-2"
            inputMode="numeric"
            maxLength={6}
          />
        </div>

        <button
          type="button"
          className="rounded-lg bg-primary px-4 py-2 text-white disabled:opacity-50"
          disabled={token.length < 6}
          onClick={() => setTokenValidated(token)}
        >
          Validar código
        </button>
      </div>
    )
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(handleResetPassword)}
        className={cn('grid gap-6', className)}
      >
        {children}
        <div className="text-center text-sm font-sans text-gray-800 dark:text-gray-100">
          <span className="mr-1">Código incorreto ou expirado?</span>
          <button
            type="button"
            className="text-primary underline underline-offset-4"
            onClick={() => setTokenValidated(false)}
          >
            Validar outro código
          </button>
        </div>
      </form>
    </FormProvider>
  )
}
